import {
  AnimationClip,
  type KeyframeTrack,
  Quaternion,
  QuaternionKeyframeTrack,
  VectorKeyframeTrack
} from 'three'

/**
 * ClipBlender
 *
 * Bake the weighted blend of two animation clips into a new clip.
 * For each track present in both source clips (matched by name), the new
 * clip's track is the per-keyframe blend at the union of timestamps:
 *   - Quaternion tracks: slerp(A, B, weight)
 *   - Vector / scalar tracks: lerp(A, B, weight)
 *
 * Tracks present in only one clip pass through at full strength (with a
 * ramp by `weight` for the side they belong to). Output clip duration is
 * the longer of the two inputs.
 *
 * Useful for blocking out variations: "60% walk + 40% run" produces a
 * jog-like cycle without re-recording.
 */

export interface BlendOptions {
  /** Number of evenly-spaced output keyframes per second of the new clip. Default 30. */
  sample_fps?: number
}

export class ClipBlender {
  static blend (
    clip_a: AnimationClip,
    clip_b: AnimationClip,
    weight: number,
    options: BlendOptions = {}
  ): AnimationClip {
    const w = Math.max(0, Math.min(1, weight))
    const sample_fps = options.sample_fps ?? 30
    const duration = Math.max(clip_a.duration, clip_b.duration)
    const sample_count = Math.max(2, Math.floor(duration * sample_fps) + 1)

    // Build a name → track index for each clip.
    const tracks_a = new Map<string, KeyframeTrack>()
    for (const t of clip_a.tracks) tracks_a.set(t.name, t)
    const tracks_b = new Map<string, KeyframeTrack>()
    for (const t of clip_b.tracks) tracks_b.set(t.name, t)

    const all_names = new Set<string>([...tracks_a.keys(), ...tracks_b.keys()])
    const new_tracks: KeyframeTrack[] = []
    const sample_times: number[] = new Array(sample_count)
    for (let i = 0; i < sample_count; i++) sample_times[i] = (i / (sample_count - 1)) * duration

    for (const name of all_names) {
      const ta = tracks_a.get(name)
      const tb = tracks_b.get(name)

      const reference = ta ?? tb
      if (reference === undefined) continue
      const value_size = reference.getValueSize()
      const is_quaternion = name.endsWith('.quaternion') && value_size === 4

      const out_values: number[] = []
      for (const t of sample_times) {
        const sa = ta !== undefined ? sample_track(ta, t, value_size) : null
        const sb = tb !== undefined ? sample_track(tb, t, value_size) : null
        let blended: number[]
        if (sa !== null && sb !== null) {
          blended = is_quaternion ? slerp_arrays(sa, sb, w) : lerp_arrays(sa, sb, w)
        } else if (sa !== null) {
          // Only A has data — keep A's value but reduce influence by `1 - w`.
          // For quaternions, slerp from identity (no rotation) toward sa by (1-w).
          blended = is_quaternion ? slerp_toward_identity(sa, 1 - w) : scale_array(sa, 1 - w)
        } else if (sb !== null) {
          blended = is_quaternion ? slerp_toward_identity(sb, w) : scale_array(sb, w)
        } else {
          continue
        }
        for (const v of blended) out_values.push(v)
      }

      if (is_quaternion) {
        new_tracks.push(new QuaternionKeyframeTrack(name, sample_times, out_values))
      } else {
        new_tracks.push(new VectorKeyframeTrack(name, sample_times, out_values))
      }
    }

    return new AnimationClip(`Blend ${clip_a.name} + ${clip_b.name}`, duration, new_tracks)
  }
}

function sample_track (track: KeyframeTrack, time: number, value_size: number): number[] | null {
  const n = track.times.length
  if (n === 0) return null
  if (time <= track.times[0]) return read(track.values, 0, value_size)
  if (time >= track.times[n - 1]) return read(track.values, (n - 1) * value_size, value_size)
  let lo = 0; let hi = n - 1
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (track.times[mid] <= time) lo = mid
    else hi = mid
  }
  const t0 = track.times[lo]
  const t1 = track.times[hi]
  const alpha = (time - t0) / (t1 - t0)
  const a = read(track.values, lo * value_size, value_size)
  const b = read(track.values, hi * value_size, value_size)
  if (value_size === 4) return slerp_arrays(a, b, alpha)
  return lerp_arrays(a, b, alpha)
}

function read (values: ArrayLike<number>, offset: number, size: number): number[] {
  const out: number[] = new Array(size)
  for (let i = 0; i < size; i++) out[i] = values[offset + i]
  return out
}

function lerp_arrays (a: number[], b: number[], w: number): number[] {
  const out: number[] = new Array(a.length)
  for (let i = 0; i < a.length; i++) out[i] = a[i] + (b[i] - a[i]) * w
  return out
}

function scale_array (a: number[], s: number): number[] {
  const out: number[] = new Array(a.length)
  for (let i = 0; i < a.length; i++) out[i] = a[i] * s
  return out
}

const TMP_QUAT_A_BLEND = new Quaternion()
const TMP_QUAT_B_BLEND = new Quaternion()
function slerp_arrays (a: number[], b: number[], w: number): number[] {
  TMP_QUAT_A_BLEND.set(a[0], a[1], a[2], a[3])
  TMP_QUAT_B_BLEND.set(b[0], b[1], b[2], b[3])
  TMP_QUAT_A_BLEND.slerp(TMP_QUAT_B_BLEND, w)
  return [TMP_QUAT_A_BLEND.x, TMP_QUAT_A_BLEND.y, TMP_QUAT_A_BLEND.z, TMP_QUAT_A_BLEND.w]
}

const TMP_QUAT_IDENTITY = new Quaternion()
function slerp_toward_identity (q: number[], strength: number): number[] {
  TMP_QUAT_A_BLEND.set(q[0], q[1], q[2], q[3])
  TMP_QUAT_IDENTITY.set(0, 0, 0, 1)
  TMP_QUAT_IDENTITY.slerp(TMP_QUAT_A_BLEND, strength)
  return [TMP_QUAT_IDENTITY.x, TMP_QUAT_IDENTITY.y, TMP_QUAT_IDENTITY.z, TMP_QUAT_IDENTITY.w]
}
